// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * DOM rendering. Every view is a pure function of plain data + callbacks, so the
 * controller in main.ts owns all state and the network, and this module owns only
 * pixels. The secret is written to the DOM only in the receiver's reveal view and
 * is never persisted anywhere.
 */
import QRCode from 'qrcode';
import { icon } from './icons.ts';
import { fmtChars } from './format.ts';

export interface ShellRefs {
  view: HTMLElement;
  drawer: HTMLElement;
  drawerBody: HTMLElement;
}

export interface NavHandlers {
  how: () => void;
  privacy: () => void;
  about: () => void;
  toggleLog: () => void;
}

function h<T extends HTMLElement = HTMLElement>(html: string): T {
  const tpl = document.createElement('template');
  tpl.innerHTML = html.trim();
  return tpl.content.firstElementChild as T;
}

/** Build the persistent chrome. Returns refs the controller renders views into. */
export function renderShell(app: HTMLElement, nav: NavHandlers): ShellRefs {
  app.innerHTML = '';
  const shell = h(`
    <div class="app-shell">
      <header class="topbar">
        <div class="brand">
          <span class="brand-mark">${icon('flame')}</span>
          <span class="brand-name">Burnwell</span>
        </div>
        <nav class="topnav">
          <button type="button" class="nav-btn" id="nav-how">${icon('help')}<span>How it works</span></button>
          <button type="button" class="nav-btn" id="nav-privacy">${icon('shield')}<span>Privacy</span></button>
          <button type="button" class="nav-btn" id="nav-about">${icon('info')}<span>About</span></button>
          <button type="button" class="nav-btn" id="nav-log" aria-label="Event log">${icon('logDrawer')}<span>Log</span></button>
        </nav>
      </header>
      <button type="button" class="trust-banner" id="trust-banner">
        ${icon('shieldCheck')}
        <span><strong>Runs entirely in your browser.</strong> The secret is handed straight to the recipient, end-to-end encrypted, shown once, then burned — nothing is stored on any server.</span>
      </button>
      <main class="main-content"><div id="view"></div></main>
      <footer class="site-footer">
        <div class="footer-inner">
          Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
          · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
        </div>
      </footer>
    </div>`);
  app.appendChild(shell);

  const drawer = h(`<aside class="drawer" id="drawer" aria-hidden="true"><div id="drawer-body"></div></aside>`);
  document.body.appendChild(drawer);

  shell.querySelector('#nav-how')!.addEventListener('click', nav.how);
  shell.querySelector('#nav-privacy')!.addEventListener('click', nav.privacy);
  shell.querySelector('#nav-about')!.addEventListener('click', nav.about);
  shell.querySelector('#nav-log')!.addEventListener('click', nav.toggleLog);
  shell.querySelector('#trust-banner')!.addEventListener('click', nav.privacy);

  return {
    view: shell.querySelector('#view') as HTMLElement,
    drawer,
    drawerBody: drawer.querySelector('#drawer-body') as HTMLElement,
  };
}

function sasHtml(words: string[]): string {
  return `
    <div class="sas-box">
      <div class="sas-head">${icon('shieldCheck')} Read these four words aloud — both devices must show the same ones</div>
      <div class="sas-words">${words.map((w) => `<span class="sas-word">${w}</span>`).join('')}</div>
    </div>`;
}

async function qrInto(box: HTMLElement, url: string): Promise<void> {
  try {
    const dataUrl = await QRCode.toDataURL(url, { margin: 1, width: 320, color: { dark: '#0c0a09', light: '#ffffff' } });
    box.innerHTML = `<img alt="Burn link QR code" src="${dataUrl}" />`;
  } catch {
    box.innerHTML = `<span class="qr-fail">QR unavailable</span>`;
  }
}

// ── Sender views ─────────────────────────────────────────

export interface ComposeHandlers {
  onCreate: (secret: string, label: string, splitMode: boolean) => void;
}

export function renderSenderCompose(view: HTMLElement, handlers: ComposeHandlers): void {
  view.innerHTML = '';
  const card = h(`
    <section class="card">
      <h1 class="card-title">Send a secret, safely</h1>
      <p class="card-sub">Type a password, API key or recovery phrase. Burnwell hands it straight to the recipient — end-to-end encrypted, shown once, then burned. Nothing is ever stored on a server.</p>

      <label class="field-label" for="secret-input">The secret</label>
      <div class="secret-wrap">
        <textarea id="secret-input" class="compose-input secret-input" rows="3" spellcheck="false" autocomplete="off" autocapitalize="off" autocorrect="off" placeholder="Paste or type the secret to hand over…"></textarea>
        <button type="button" class="secret-toggle" id="secret-toggle" aria-label="Hide secret">${icon('eye')}</button>
      </div>
      <div class="compose-meta"><span id="secret-count" class="muted small">0 characters</span></div>

      <label class="field-label" for="label-input" style="margin-top:14px">Label <span class="muted">(optional, not secret — helps the recipient know what it is)</span></label>
      <input id="label-input" class="text-input" type="text" maxlength="80" spellcheck="false" placeholder="e.g. Wi-Fi password, AWS root key" />

      <div class="mode-row">
        <div class="section-label">Link type</div>
        <label class="mode-opt"><input type="radio" name="mode" value="full" checked /><div><strong>Full link</strong><span>The passphrase is inside the link. Simplest — just send the link.</span></div></label>
        <label class="mode-opt"><input type="radio" name="mode" value="split" /><div><strong>Split (more secure)</strong><span>The link carries only the room. You read the four-word passphrase to the recipient separately.</span></div></label>
      </div>

      <div class="compose-actions">
        <button type="button" class="btn btn-primary" id="create-btn">${icon('flame')} Create burn link</button>
      </div>
      <p class="compose-hint muted small">Both of you need to be online at the same moment — there’s no server holding the secret for later. <kbd>⌘</kbd><kbd>Enter</kbd> to create.</p>
    </section>`);
  view.appendChild(card);

  const input = card.querySelector('#secret-input') as HTMLTextAreaElement;
  const count = card.querySelector('#secret-count') as HTMLElement;
  const toggle = card.querySelector('#secret-toggle') as HTMLButtonElement;
  const label = card.querySelector('#label-input') as HTMLInputElement;
  const createBtn = card.querySelector('#create-btn') as HTMLButtonElement;

  const update = () => {
    count.textContent = fmtChars([...input.value].length);
  };
  input.addEventListener('input', update);

  let hidden = false;
  toggle.addEventListener('click', () => {
    hidden = !hidden;
    input.classList.toggle('masked', hidden);
    toggle.innerHTML = icon(hidden ? 'eyeOff' : 'eye');
    toggle.setAttribute('aria-label', hidden ? 'Show secret' : 'Hide secret');
  });

  const submit = () => {
    const secret = input.value;
    if (!secret.trim()) {
      input.focus();
      input.classList.add('shake');
      setTimeout(() => input.classList.remove('shake'), 400);
      return;
    }
    const splitMode = (card.querySelector('input[name="mode"]:checked') as HTMLInputElement).value === 'split';
    handlers.onCreate(secret, label.value.trim(), splitMode);
  };
  createBtn.addEventListener('click', submit);
  input.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  });
  input.focus();
}

export interface ArmedData {
  link: string;
  words: string[];
  splitMode: boolean;
  passphrase: string;
  label: string;
  chars: number;
  peerCount: number;
}

export interface ArmedHandlers {
  onCopyLink: () => void;
  onShare: () => void;
  onCopyPass: () => void;
  onCancel: () => void;
}

export function renderSenderArmed(view: HTMLElement, data: ArmedData, handlers: ArmedHandlers): void {
  view.innerHTML = '';
  const shareSupported = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const card = h(`
    <section class="card">
      <div class="status-row">
        <span class="status-dot" data-status="waiting"></span>
        <span class="status-text" id="armed-status">Waiting for the recipient to connect…</span>
        <button type="button" class="btn btn-danger btn-sm" id="cancel-btn">${icon('trash')} Cancel &amp; burn</button>
      </div>

      <p class="card-sub" style="margin-top:14px">Send this link to your recipient. The secret (${data.label ? `<strong>${escapeAttr(data.label)}</strong>, ` : ''}${fmtChars(data.chars)}) is held only in this tab and released once, to the first person who verifies and reveals it.</p>

      <div class="pair-grid">
        <div class="pair-left">
          <div>
            <span class="field-label">Burn link</span>
            <div class="link-row">
              <input id="link-input" class="link-input" readonly value="${escapeAttr(data.link)}" />
              <button type="button" class="btn" id="copy-link">${icon('copy')} Copy</button>
              ${shareSupported ? `<button type="button" class="btn" id="share-link">${icon('share')}</button>` : ''}
            </div>
          </div>
          ${
            data.splitMode
              ? `<div class="pass-box">
                   <div class="pass-head">${icon('key')} Read this passphrase to the recipient separately</div>
                   <div class="pass-row"><code class="pass-value">${escapeAttr(data.passphrase)}</code>
                   <button type="button" class="btn btn-sm" id="copy-pass">${icon('copy')} Copy</button></div>
                   <p class="muted small">The link above does not contain it — so the link alone can’t open the secret.</p>
                 </div>`
              : ''
          }
          ${sasHtml(data.words)}
          <div class="extra-peer" id="extra-peer" ${data.peerCount > 1 ? '' : 'hidden'}>
            ${icon('alert')} More than one device has connected — only the first to reveal receives the secret.
          </div>
        </div>
        <div class="pair-right">
          <div class="qr-box" id="qr-box"></div>
          <span class="qr-cap">${icon('qr')} Scan on a phone</span>
        </div>
      </div>
    </section>`);
  view.appendChild(card);

  void qrInto(card.querySelector('#qr-box') as HTMLElement, data.link);
  card.querySelector('#copy-link')!.addEventListener('click', handlers.onCopyLink);
  card.querySelector('#share-link')?.addEventListener('click', handlers.onShare);
  card.querySelector('#copy-pass')?.addEventListener('click', handlers.onCopyPass);
  card.querySelector('#cancel-btn')!.addEventListener('click', handlers.onCancel);
}

/** Update just the status line + extra-peer warning without a full re-render. */
export function updateSenderStatus(view: HTMLElement, text: string, status: string, peerCount: number): void {
  const dot = view.querySelector('.status-dot') as HTMLElement | null;
  const label = view.querySelector('#armed-status') as HTMLElement | null;
  const extra = view.querySelector('#extra-peer') as HTMLElement | null;
  if (dot) dot.dataset.status = status;
  if (label) label.textContent = text;
  if (extra) extra.hidden = peerCount <= 1;
}

export function renderSenderBurned(view: HTMLElement, when: string, onAgain: () => void): void {
  view.innerHTML = '';
  const card = h(`
    <section class="card center-card">
      <div class="result-icon good">${icon('checks')}</div>
      <h1 class="card-title">Delivered &amp; burned</h1>
      <p class="card-sub center">Your recipient opened the secret at <strong>${escapeAttr(when)}</strong>. It has been wiped from this device and the connection closed. Nothing was ever stored on a server — the secret now exists only where your recipient copied it.</p>
      <button type="button" class="btn btn-primary" id="again-btn">${icon('flame')} Send another</button>
    </section>`);
  view.appendChild(card);
  card.querySelector('#again-btn')!.addEventListener('click', onAgain);
}

export function renderSenderCancelled(view: HTMLElement, onAgain: () => void): void {
  view.innerHTML = '';
  const card = h(`
    <section class="card center-card">
      <div class="result-icon">${icon('trash')}</div>
      <h1 class="card-title">Cancelled</h1>
      <p class="card-sub center">The link was burned before anyone opened it — the secret was never sent and is gone from this device.</p>
      <button type="button" class="btn btn-primary" id="again-btn">${icon('flame')} Start over</button>
    </section>`);
  view.appendChild(card);
  card.querySelector('#again-btn')!.addEventListener('click', onAgain);
}

// ── Receiver views ───────────────────────────────────────

export function renderReceiverPassEntry(view: HTMLElement, onConnect: (pass: string) => void): void {
  view.innerHTML = '';
  const card = h(`
    <section class="card">
      <h1 class="card-title">Enter the passphrase</h1>
      <p class="card-sub">This is a <strong>split</strong> link — it doesn’t contain the passphrase. Type the four words the sender gave you to open the secure connection.</p>
      <label class="field-label" for="pass-input">Passphrase</label>
      <input id="pass-input" class="text-input" type="text" spellcheck="false" autocomplete="off" autocapitalize="off" placeholder="e.g. amber-cedar-otter-quartz" />
      <div class="compose-actions">
        <button type="button" class="btn btn-primary" id="connect-btn">${icon('lock')} Connect</button>
      </div>
    </section>`);
  view.appendChild(card);
  const input = card.querySelector('#pass-input') as HTMLInputElement;
  const go = () => {
    if (!input.value.trim()) {
      input.focus();
      return;
    }
    onConnect(input.value);
  };
  card.querySelector('#connect-btn')!.addEventListener('click', go);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') go();
  });
  input.focus();
}

export function renderReceiverConnecting(view: HTMLElement): void {
  view.innerHTML = '';
  const card = h(`
    <section class="card center-card">
      <div class="result-icon spin">${icon('refresh')}</div>
      <h1 class="card-title">Connecting…</h1>
      <p class="card-sub center">Finding the sender and opening a direct, encrypted channel. Keep this tab open — the secret only moves while you’re both connected.</p>
    </section>`);
  view.appendChild(card);
}

export interface ReadyHandlers {
  onReveal: () => void;
}

export function renderReceiverReady(view: HTMLElement, words: string[], handlers: ReadyHandlers): void {
  view.innerHTML = '';
  const card = h(`
    <section class="card">
      <div class="status-row">
        <span class="status-dot" data-status="connected"></span>
        <span class="status-text">Connected to the sender</span>
      </div>
      <h1 class="card-title" style="margin-top:14px">A secret is waiting for you</h1>
      <p class="card-sub">First, confirm the four words below match what the sender told you. Then reveal the secret — it’s shown <strong>once</strong> and isn’t stored anywhere, so copy it before you close the tab.</p>
      ${sasHtml(words)}
      <div class="compose-actions" style="margin-top:18px">
        <button type="button" class="btn btn-primary" id="reveal-btn">${icon('eye')} Reveal secret</button>
      </div>
    </section>`);
  view.appendChild(card);
  card.querySelector('#reveal-btn')!.addEventListener('click', handlers.onReveal);
}

export function renderReceiverRequesting(view: HTMLElement): void {
  const btn = view.querySelector('#reveal-btn') as HTMLButtonElement | null;
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = `${icon('refresh')} Receiving…`;
  }
}

export interface RevealedHandlers {
  onCopy: () => void;
  onBurn: () => void;
}

export function renderReceiverRevealed(
  view: HTMLElement,
  secret: string,
  label: string,
  handlers: RevealedHandlers,
): void {
  view.innerHTML = '';
  const card = h(`
    <section class="card">
      <div class="status-row">
        <span class="status-dot" data-status="delivered"></span>
        <span class="status-text">Secret revealed — shown once</span>
      </div>
      ${label ? `<div class="reveal-label">${icon('key')} ${escapeAttr(label)}</div>` : ''}
      <div class="secret-reveal" id="secret-reveal"></div>
      <div class="compose-actions">
        <button type="button" class="btn btn-primary" id="copy-secret">${icon('copy')} Copy</button>
        <button type="button" class="btn btn-danger" id="burn-secret">${icon('flame')} Burn from screen</button>
      </div>
      <p class="compose-hint muted small">${icon('alert')} This secret isn’t saved anywhere. If you close or refresh this tab it’s gone — copy it now if you need it.</p>
    </section>`);
  view.appendChild(card);
  // Assign as textContent so the secret is never interpolated into HTML.
  (card.querySelector('#secret-reveal') as HTMLElement).textContent = secret;
  card.querySelector('#copy-secret')!.addEventListener('click', handlers.onCopy);
  card.querySelector('#burn-secret')!.addEventListener('click', handlers.onBurn);
}

export function renderReceiverBurned(view: HTMLElement): void {
  view.innerHTML = '';
  const card = h(`
    <section class="card center-card">
      <div class="result-icon good">${icon('check')}</div>
      <h1 class="card-title">Burned</h1>
      <p class="card-sub center">The secret has been cleared from this screen. It was never saved, and the sender’s copy was already wiped when you opened it.</p>
    </section>`);
  view.appendChild(card);
}

export function renderReceiverGone(view: HTMLElement): void {
  view.innerHTML = '';
  const card = h(`
    <section class="card center-card">
      <div class="result-icon bad">${icon('alert')}</div>
      <h1 class="card-title">Already delivered</h1>
      <p class="card-sub center">This secret has already been handed to someone else and burned — a one-time link can only be opened once. If that wasn’t your intended recipient, treat the secret as compromised. Ask the sender for a fresh link.</p>
    </section>`);
  view.appendChild(card);
}

export function renderError(view: HTMLElement, message: string, onRetry?: () => void): void {
  view.innerHTML = '';
  const card = h(`
    <section class="card center-card">
      <div class="result-icon bad">${icon('alert')}</div>
      <h1 class="card-title">Something went wrong</h1>
      <p class="card-sub center">${escapeAttr(message)}</p>
      ${onRetry ? `<button type="button" class="btn btn-primary" id="retry-btn">${icon('refresh')} Try again</button>` : ''}
    </section>`);
  view.appendChild(card);
  if (onRetry) card.querySelector('#retry-btn')!.addEventListener('click', onRetry);
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
